import { useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import { t } from '../../../../common/i18n';
import { DeliveryPlace, emptyAddressDefaults } from '../../placeOrderTypes.js';
import {
  AddressSelectionOptionsCoCo,
  createEditableAddressOptions,
  formatDeliveryAddress,
} from './AddressOptionCoCo.js';
import { DeliveryStepAddressCoCo } from './DeliveryStepAddressCoCo.js';
import type { CustomerResponse } from '../../../../generated/api/customerResponse.js';
import type { DeliveryAddress } from '../../../../generated/api/deliveryAddress.js';
import type { CustomerPrimaryAddress } from '../../PlaceOrderCoCo.js';
import type { PlaceOrderFormData } from '../../placeOrderTypes.js';

import './AddressSelection.scss';

export interface AddressSelectionProps {
  customerInfo: CustomerResponse;
  onClearError?: () => void;
  customerPrimaryAddress?: CustomerPrimaryAddress;
}

export const AddressSelectionCoCo = ({ customerInfo, onClearError, customerPrimaryAddress }: AddressSelectionProps) => {
  const { setValue, watch } = useFormContext<PlaceOrderFormData>();

  const formDeliveryPlace = watch('delivery.deliveryPlace');
  const formDeliveryAddress = watch('delivery.deliveryAddress');

  const [editingAddress, setEditingAddress] = useState(false);
  const [streetSearchAddressValByPlace, setStreetSearchAddressValByPlace] = useState<
    Partial<Record<DeliveryPlace, string>>
  >({});
  const [postalCodeByPlace, setPostalCodeByPlace] = useState<Partial<Record<DeliveryPlace, string>>>({});

  const otherAddressFormMethods = useForm<DeliveryAddress>({ defaultValues: emptyAddressDefaults() });
  const homeAddressFormMethods = useForm<DeliveryAddress>({ defaultValues: emptyAddressDefaults(customerInfo.name) });

  const handleRadioChange = (place: DeliveryPlace) => {
    setValue('delivery.deliveryPlace', place);
    onClearError?.();

    if (place === DeliveryPlace.COMPANY_ADDRESS) {
      if (customerPrimaryAddress) {
        setValue('delivery.deliveryAddress', customerPrimaryAddress.deliveryAddress);
        setValue('delivery.deliveryAddressId', customerPrimaryAddress.addressId);
      }
      setEditingAddress(false);
    } else if (!formDeliveryAddress || place !== formDeliveryPlace) {
      // For non-company addresses: always open form if no address saved yet,
      // or if switching to a different place
      setValue('delivery.deliveryAddress', undefined);
      setEditingAddress(true);
    }
  };

  const handleSetAddress = (address: DeliveryAddress, addressId?: string) => {
    setValue('delivery.deliveryAddress', address);
    setValue('delivery.deliveryAddressId', addressId);
    setEditingAddress(false);
    onClearError?.();
  };

  const renderAddressEditor = (place: DeliveryPlace, methods: typeof homeAddressFormMethods) => (
    <FormProvider {...methods}>
      <DeliveryStepAddressCoCo
        companyName={customerInfo.name}
        onSetAddress={handleSetAddress}
        onCancel={() => setEditingAddress(false)}
        streetSearchAddressVal={streetSearchAddressValByPlace[place] || ''}
        postalCode={postalCodeByPlace[place] || ''}
        setPostalCode={postalCode => setPostalCodeByPlace(prev => ({ ...prev, [place]: postalCode }))}
        setStreetSearchAddressVal={streetSearchAddressVal =>
          setStreetSearchAddressValByPlace(prev => ({ ...prev, [place]: streetSearchAddressVal }))
        }
        showCompanyNameInput={place === DeliveryPlace.OTHER_ADDRESS}
      />
    </FormProvider>
  );

  const addressOptions = createEditableAddressOptions({
    deliveryAddress: formDeliveryAddress,
    deliveryPlace: formDeliveryPlace,
    editingAddress,
    homeEditor: renderAddressEditor(DeliveryPlace.HOME_ADDRESS, homeAddressFormMethods),
    homeMethods: homeAddressFormMethods,
    onSelect: handleRadioChange,
    otherEditor: renderAddressEditor(DeliveryPlace.OTHER_ADDRESS, otherAddressFormMethods),
    otherMethods: otherAddressFormMethods,
    setEditingAddress,
  });

  return (
    <div className="of-place-order__address-selection">
      <p className={dsClass.FONT_WEIGHT_BOLD}>{t.AK55('Select delivery address')}</p>

      <AddressSelectionOptionsCoCo
        companySummary={
          customerPrimaryAddress ? formatDeliveryAddress(customerPrimaryAddress.deliveryAddress) : undefined
        }
        home={addressOptions.home}
        onSelect={handleRadioChange}
        other={addressOptions.other}
        selectedPlace={formDeliveryPlace}
      />
    </div>
  );
};
